<script setup lang="ts">
import UiChevronIconS from 'ui-toolkit/components/chevron/UiChevronIconS.vue';
import type { PostData } from '@/types';
import ContentContainer from '@/components/layout/ContentContainer.vue';
import ContentColumn from '@/components/layout/ContentColumn.vue';

defineProps<Pick<PostData, 'title' | 'standfirst' | 'publishedOn' | 'authors'>>();
</script>

<template>
  <ContentContainer class="analysisHeader">
    <ContentColumn class="analysisTitle">
      <NuxtLink to="/analysis" class="backLink button-text">
        <UiChevronIconS direction="left" />
        Back to News & Analysis
      </NuxtLink>
      <h1>{{ title }}</h1>
    </ContentColumn>
    <ContentColumn class="analysisHeaderContainer">
      <template #left>
        <div class="publicationDetailsInner gridItemLeft">
          <div class="authorImages">
            <NuxtImg
              v-for="(author, key) in authors"
              :key="key"
              :src="author.image"
              :alt="author.name"
              :title="author.name"
              class="authorImage"
              width="48"
              height="48"
            />
          </div>
          <div>
            <div class="authorNames heading-6">
              <div v-for="(author, key) in authors" :key="key">
                {{ author.name }}
              </div>
            </div>
            <div class="publicationDate body-small">
              {{ publishedOn }}
            </div>
          </div>
        </div>
      </template>
      <template #default>
        <p class="standfirst">
          {{ standfirst }}
        </p>
      </template>
      <template #right>
        <ShareButton />
      </template>
    </ContentColumn>
  </ContentContainer>
</template>

<style lang="scss" scoped>
@use "@/assets/styles/shared/screen" as screen;

.analysisHeader {
  margin-bottom: var(--spacing-m);
}

.backLink {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}
.analysisTitle {
  .backLink + h1 {
    margin-top: 3.6rem;
  }

  :deep(.column-main) {
    grid-column: 1 / span 9;
  }
}

.analysisHeaderContainer {
  :deep(.column-main) {
    font-size: 1.2em;
    color: var(--colour-dark-grey);
    order: 3;

    @include screen.respond(tablet) {
      order: 1;
    }
  }

  :deep(.column-left) {
    order: 2;
  }
  :deep(.column-right) {
    order: 4;
    justify-self: end;

    @include screen.respond(tablet) {
      justify-self: inherit;
    }
  }
}

.publicationDetailsInner {
  display: flex;
}

.authorImages {
  margin-right: 1rem;
  flex-shrink: 0;

  & > img:not(:first-child) {
    margin-left: -0.8rem;
  }
}
.authorImage {
  width: 4.8rem;
  height: 4.8rem;
  object-fit: cover;
  border-radius: 2.4rem;
  border: 1px solid #fff;
}

.authorNames {
  color: var(--colour-dark-grey);
  line-height: 1.8rem;
  font-weight: 500;
}

.publicationDate {
  color: var(--colour-mid-grey);
  line-height: 2.2rem;
}
</style>
